import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";

function NotFound() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (countdown === 0) {
      navigate("/");
    }
  }, [countdown, navigate]);

  return (
    <main className="flex flex-col items-center justify-center min-h-[85vh] w-full px-6 pt-36 pb-20 bg-gradient-to-b from-white to-gray-50/50">
      <Helmet>
        <title>404 - Page Not Found | Nirantara Solutions</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-xl w-full text-center p-10 md:p-14 bg-white/70 backdrop-blur-md rounded-3xl shadow-xl shadow-gray-100 border border-gray-100/80 transition-all duration-300 hover:shadow-2xl">
        {/* Animated Icon / Illustration Placeholder */}
        <div className="flex justify-center mb-6">
          <div className="relative flex items-center justify-center w-24 h-24 rounded-full bg-orange-50 text-[#F97316] animate-pulse">
            <svg
              className="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Large 404 text with beautiful brand gradient */}
        <h1 className="text-7xl md:text-8xl font-black tracking-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[#1C486B] to-[#F97316] mb-4 select-none">
          404
        </h1>

        {/* Page Not Found message */}
        <h2 className="text-2xl md:text-3xl font-bold text-[#1C486B] tracking-tight mb-3">
          Page Not Found
        </h2>

        {/* Short friendly explanation */}
        <p className="text-[#667085] text-base md:text-lg leading-relaxed mb-6 max-w-md mx-auto">
          The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
        </p>

        {/* Redirection Countdown Indicator */}
        <div className="mb-8 max-w-xs mx-auto">
          <div className="flex justify-between text-sm font-semibold text-gray-500 mb-1.5 montserrat">
            <span>Redirecting to homepage</span>
            <span className="text-[#F97316]">{countdown}s</span>
          </div>
          <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
            <div 
              className="bg-gradient-to-r from-[#1C486B] to-[#F97316] h-full rounded-full transition-all ease-linear duration-1000"
              style={{ width: `${(countdown / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Go back homepage button */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            to="/"
            className="w-full sm:w-auto px-6 py-2.5 rounded-full font-semibold text-white text-sm md:text-base bg-gradient-to-r from-[#1C486B] to-[#F97316] hover:from-[#14344E] hover:to-[#e06610] active:scale-95 shadow-md shadow-[#1c486b]/10 hover:shadow-lg transition-all duration-200 ease-in-out whitespace-nowrap"
            aria-label="Navigate back to the homepage"
          >
            Go Back to Homepage
          </Link>
          <Link
            to="/contact-us"
            className="w-full sm:w-auto px-6 py-2.5 rounded-full font-semibold text-[#1C486B] hover:text-[#F97316] text-sm md:text-base bg-gray-50 hover:bg-gray-100 active:scale-95 border border-gray-200 transition-all duration-200 ease-in-out whitespace-nowrap"
            aria-label="Contact Nirantara support"
          >
            Contact Support
          </Link>
        </div>
      </div>
    </main>
  );
}

export default NotFound;
